import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Trash2, Download, Table as TableIcon } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/**
 * BudgetsUploadCard — admins drop a monthly-budget CSV (property_id, month, category,
 * budget_amount [, fiscal_year]) and see it reflected on the Finance dashboard.
 *
 * Compact prop = true renders a slimmer version suited for the Finance page toolbar.
 */
export function BudgetsUploadCard({ compact = false, defaultPropertyId = null, onUploaded = () => {} }) {
  const { properties } = useProperty();
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const fyNow = new Date().getUTCFullYear();
  const [filterFy] = useState(fyNow);

  const load = useCallback(async () => {
    try {
      const params = { fiscal_year: filterFy };
      if (defaultPropertyId && defaultPropertyId !== "all") params.property_id = defaultPropertyId;
      const { data } = await api.get("/admin/budgets", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load budgets");
    }
  }, [filterFy, defaultPropertyId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("replace", String(replace));
      const { data } = await api.post("/admin/budgets/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${data.inserted} new, ${data.updated} updated`);
      onUploaded(data);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this budget row?")) return;
    try {
      await api.delete(`/admin/budgets/${id}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Delete failed");
    }
  };

  const templateHref = `${API}/admin/budgets/template.csv`;

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const key = `${r.property_id}::${r.fiscal_year}`;
      (g[key] = g[key] || { property_id: r.property_id, fiscal_year: r.fiscal_year, rows: [], total: 0 }).rows.push(r);
      g[key].total += Number(r.budget_amount || 0);
    }
    return Object.values(g).sort((a, b) => (a.property_id > b.property_id ? 1 : -1));
  }, [rows]);

  const propName = (pid) => properties.find((p) => p.id === pid)?.name || pid;

  return (
    <Card className={"border-white/10 bg-card " + (compact ? "p-3" : "p-5")} data-testid="budgets-upload-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="overline">Budgets</div>
          <div className="text-base font-semibold font-display mt-0.5">
            Annual budget targets (FY {fyNow})
          </div>
          {!compact && (
            <div className="text-xs text-zinc-500 mt-1">
              Upload a CSV of monthly targets per community. Powers the &quot;$X under budget&quot; pill on the Finance dashboard.
            </div>
          )}
        </div>
        <a
          href={templateHref}
          className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1"
          data-testid="budget-template-download"
        >
          <Download className="h-3 w-3" /> Template
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="budget-file-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
          data-testid="budget-upload-btn"
        >
          <Upload className="h-3.5 w-3.5 mr-1" />
          {busy ? "Uploading…" : "Upload CSV"}
        </Button>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Switch id="budget-replace" checked={replace} onCheckedChange={setReplace} data-testid="budget-replace-switch" />
          <Label htmlFor="budget-replace" className="cursor-pointer">Replace existing rows</Label>
        </div>
        <div className="ml-auto text-[11px] text-zinc-500 inline-flex items-center gap-1">
          <TableIcon className="h-3 w-3" /> {rows.length} rows loaded
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="budget-empty">
          <FileText className="h-4 w-4 mx-auto mb-2 opacity-60" />
          No budgets uploaded yet. Download the template above, fill it in, and drop it here.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div key={`${g.property_id}-${g.fiscal_year}`} className="rounded-md border border-white/5" data-testid={`budget-group-${g.property_id}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03]">
                <div className="text-xs">
                  <span className="text-zinc-200 font-medium">{propName(g.property_id)}</span>
                  <span className="text-zinc-500 mx-2">·</span>
                  <span className="text-zinc-400">FY {g.fiscal_year}</span>
                </div>
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 tabular-nums">
                  {money(g.total)}
                </Badge>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-white/[0.01]">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-zinc-500">Month</th>
                    <th className="text-left px-3 py-1.5 text-zinc-500">Category</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500">Amount</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.slice(0, compact ? 6 : 24).map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-3 py-1 text-zinc-400 tabular-nums">{r.month}</td>
                      <td className="px-3 py-1 text-zinc-300">{(r.category || "").replace(/_/g, " ")}</td>
                      <td className="px-3 py-1 text-right text-white tabular-nums">{money(r.budget_amount)}</td>
                      <td className="px-3 py-1 text-right">
                        <button
                          onClick={() => remove(r.id)}
                          className="text-zinc-500 hover:text-red-400"
                          title="Delete"
                          data-testid={`budget-row-delete-${r.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {g.rows.length > (compact ? 6 : 24) && (
                    <tr>
                      <td colSpan={4} className="px-3 py-1.5 text-zinc-500 text-center italic">
                        …and {g.rows.length - (compact ? 6 : 24)} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default BudgetsUploadCard;
